import { z } from 'zod';
import { ZodError } from 'zod-validation-error';
import { isValidPhoneNum } from '../isValidPhoneNum';

export const stringConstraints = (message: string) =>
  z.string().refine((val) => val.length > 0, {
    message,
  });

/**
 * function to validate the field should not be empty
 * @return
 */
export const stringFieldSchema = (fieldName: string, message: string) =>
  z.object({
    [fieldName]: z.string().refine(
      (val) => {
        return val.length > 0;
      },
      {
        message,
      },
    ),
  });

export const booleanSchema = (fieldName: string, message: string) =>
  z.object({
    [fieldName]: z.boolean().refine(
      (val) => {
        return val !== null;
      },
      {
        message,
      },
    ),
  });

const phoneNumberValidation = (message: string, messageInvalid: string) =>
  z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        message,
        code: z.ZodIssueCode.custom,
      });
    } else if (!isValidPhoneNum(val)) {
      ctx.addIssue({
        message: messageInvalid,
        code: z.ZodIssueCode.custom,
      });
    }
  });

type TextAreaMaxLengthSchemaType = {
  fieldNames: string[];
  maxLength: number;
  maxLengthMessage?: string;
};

export const yourDetailsSchema = z.object({
  firstName: stringConstraints('Enter a first name'),
  lastName: stringConstraints('Enter a last name'),
  email: z.string().email('Enter a valid email address'),
});

export const noiseYourDetailsSchema = z.object({
  firstName: stringConstraints('Please enter your first name'),
  lastName: stringConstraints('Please enter your last name'),
  email: z.string().email('Please enter your email address'),
  phoneNumber: phoneNumberValidation(
    'Please enter your phone number',
    'Enter a valid phone number',
  ),
});

export const addressResultSchema = z.object({
  addressSearch: z
    .array(z.any())
    .nonempty('No address found. Please try again.'),
});

export const parseZodErrors = (error: ZodError) => {
  return error.errors.map((err) => ({
    path: err.path[0] as string,
    message: err.message,
  }));
};

export const binaryQuestionSchema = () =>
  z.object({
    receiveConfirmationEmail: z.string().refine(
      (val) => {
        return val.length > 0;
      },
      {
        message: 'Select an option',
      },
    ),
  });

export const radioOptionsSchema = (fieldName: string) =>
  z.object({
    [fieldName]: z.string().refine(
      (val) => {
        return val.length > 0;
      },
      { message: 'Select an option' },
    ),
  });

export const locationValidation = z.object({
  locationOfWaste: z.string().refine((val) => val.length > 0, {
    message: 'Please select a location on the map',
  }),
});

export const issueLocationValidation = z.object({
  locationIssue: z.string().refine((val) => val.length > 0, {
    message: 'Please select a location on the map',
  }),
});

export const wasteCurrentSubcategory = z.object({
  subcategoryName: z.string().refine((val) => val.length > 0, {
    message: 'Select a category to continue',
  }),
});
export const inferCategoryValidation = z.object({
  infercategoryName: z.string().refine((val) => val.length > 0, {
    message: 'Select a category to continue',
  }),
});

export const validateDynamicFields = (fieldName: string, response: string) => {
  return z.object({
    [fieldName]: z.string().refine(
      () => {
        return response.length > 0;
      },
      {
        message: 'Select an option to continue',
      },
    ),
  });
};

export const TextAreaMaxLengthSchema = ({
  fieldNames,
  maxLength,
  maxLengthMessage,
}: TextAreaMaxLengthSchemaType) => {
  const textAreaValidation = fieldNames.reduce(
    (rest, fieldName) => ({
      ...rest,
      [fieldName]: z.string().refine(
        (val) => {
          return val.length <= maxLength;
        },
        {
          message: maxLengthMessage || 'Field must be 500 characters or less',
        },
      ),
    }),
    {},
  );
  return z.object(textAreaValidation);
};

export type TextAreaFlexibleLengthSchemaType = {
  fieldNames: string[];
  minLength?: number;
  maxLength: number;
};

export const TextAreaFlexibleLengthSchema = ({
  fieldNames,
  minLength,
  maxLength,
}: TextAreaFlexibleLengthSchemaType) => {
  const textAreaValidation = fieldNames.reduce(
    (rest, fieldName) => ({
      ...rest,
      [fieldName]: z
        .string()
        .min(
          minLength || 1,
          minLength
            ? `Field must be at least ${minLength} characters`
            : 'Tell us more about the problem is required',
        )
        .max(maxLength, `Field must be ${maxLength} characters or less`),
    }),
    {},
  );
  return z.object(textAreaValidation);
};

export type TextAreaInputSchemaType = {
  fieldName: string;
  requiredMessage: string;
  maxLength: number;
  maxLengthMessage: string;
};

export const textAreaInputSchema = ({
  fieldName,
  requiredMessage,
  maxLength,
  maxLengthMessage,
}: TextAreaInputSchemaType) =>
  z.object({
    [fieldName]: z
      .string()
      .refine(
        (val) => {
          return val.length > 0;
        },
        {
          message: requiredMessage,
        },
      )
      .refine(
        (val) => {
          return val.length <= maxLength;
        },
        {
          message: maxLengthMessage,
        },
      ),
  });

export const dateAndTimeSchema = z.object({
  selectedDay: stringConstraints('day is required'),
  selectedMonth: stringConstraints('month is required'),
  selectedYear: stringConstraints('year is required'),
  selectedMinute: stringConstraints('minute is required'),
  selectedHour: stringConstraints('hour is required'),
});
const currentYear = new Date().getFullYear();

export const dateAndTimeRangeSchema = z
  .object({
    dayRange: z.preprocess(
      (val) => String(val),
      z.string().refine((val) => /^\d+$/.test(val), {
        message: 'Day must be a number',
      }),
    ),
    monthRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Month must be a number',
        })

        .transform((val) => Number(val))
        .refine((val) => val >= 1 && val <= 12, {
          message: 'Month must be between 1 and 12',
        }),
    ),
    yearRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Year must be a number',
        })
        .transform((val) => Number(val))
        .refine((val) => val >= 1975 && val <= currentYear, {
          message: `Year must be between 1975 and ${currentYear}`,
        }),
    ),
    minuteRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Minute must be a number',
        })
        .transform((val) => Number(val))
        .refine((val) => val >= 0 && val <= 59, {
          message: 'Minute must be between 0 and 59',
        }),
    ),
    hourRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Hour must be a number',
        })
        .transform((val) => Number(val))
        .refine((val) => val >= 0 && val <= 23, {
          message: 'Hour must be between 0 and 23',
        }),
    ),
  })
  .superRefine(
    ({ dayRange, monthRange, yearRange, hourRange, minuteRange }, ctx) => {
      const firstDate = new Date(
        yearRange,
        Number(monthRange) - 1,
        1,
      ).getDate();
      const lastDate = new Date(yearRange, Number(monthRange), 0).getDate();
      const currentMonth = new Date().getMonth() + 1;
      const currentDay = new Date().getDate();
      const currentHour = new Date().getHours();
      const currentMinute = new Date().getMinutes();
      if (Number(dayRange) < firstDate || Number(dayRange) > lastDate) {
        ctx.addIssue({
          message: `Day must be between ${firstDate} and ${lastDate}`,
          code: z.ZodIssueCode.custom,
          path: ['dayRange'],
        });
      }
      // below validations are to prevent user from entering future date and time
      if (
        yearRange === currentYear &&
        monthRange > currentMonth &&
        monthRange <= 12
      ) {
        ctx.addIssue({
          message: `Month must be between 1 and ${currentMonth}`,
          code: z.ZodIssueCode.custom,
          path: ['monthRange'],
        });
      }
      if (
        yearRange === currentYear &&
        monthRange === currentMonth &&
        Number(dayRange) > currentDay &&
        Number(dayRange) <= lastDate
      ) {
        ctx.addIssue({
          message: `Day must be between ${firstDate} and ${currentDay}`,
          code: z.ZodIssueCode.custom,
          path: ['dayRange'],
        });
      }
      if (
        yearRange === currentYear &&
        monthRange === currentMonth &&
        Number(dayRange) === currentDay &&
        hourRange > currentHour &&
        hourRange < 24
      ) {
        ctx.addIssue({
          message: `Hour must be between 0 and ${currentHour}`,
          code: z.ZodIssueCode.custom,
          path: ['hourRange'],
        });
      }
      if (
        yearRange === currentYear &&
        monthRange === currentMonth &&
        Number(dayRange) === currentDay &&
        hourRange === currentHour &&
        minuteRange > currentMinute &&
        minuteRange < 60
      ) {
        ctx.addIssue({
          message: `Minute must be between 0 and ${currentMinute}`,
          code: z.ZodIssueCode.custom,
          path: ['minuteRange'],
        });
      }
    },
  );
